import { lazy, Suspense } from 'react';
import { Navigate } from 'react-router-dom';
import Layout from '../components/Layout.jsx';
import ProtectedRoute from '../components/ProtectedRoute.jsx';
import GuestRoute from '../components/GuestRoute.jsx';
import FeatureRoute from '../components/FeatureRoute.jsx';
import { FEATURE_CODES } from '../constants/featureElements.js';

// Lazy load pages
const NotFoundPage = lazy(() => import('../pages/NotFound.jsx'));

// Lazy load reorganized pages
const FintechDashboardPage = lazy(() => import('../pages/FintechDashboard/index.jsx'));
const ComparePlansPage = lazy(() => import('../pages/ComparePlans/index.jsx'));
const PortfolioPage = lazy(() => import('../pages/Portfolio/index.jsx'));
const CanslimAnalysisPage = lazy(() => import('../pages/CanslimAnalysis/index.jsx'));
const SignalsAlertsPage = lazy(() => import('../pages/SignalsAlerts/index.jsx'));
const WatchlistNotesPage = lazy(() => import('../pages/WatchlistNotes/index.jsx'));
const LoginPage = lazy(() => import('../pages/Auth/Login.jsx'));
const RegisterPage = lazy(() => import('../pages/Auth/Register.jsx'));
const GoogleCallbackPage = lazy(() => import('../pages/Auth/GoogleCallback.jsx'));
const PersonaLensPage = lazy(() => import('../pages/PersonaLens/index.jsx'));
const SearchStockPage = lazy(() => import('../pages/SearchStock/index.jsx'));
const VideoStudioPage = lazy(() => import('../pages/VideoStudio/index.jsx'));
const CanslimPatternsPage = lazy(() => import('../pages/CanslimPatterns/index.jsx'));
const BreakoutToolPage = lazy(() => import('../pages/BreakoutTool/index.jsx'));
const StockDetailPage = lazy(() => import('../pages/StockDetail/index.jsx'));
const StockChartStandalonePage = lazy(() => import('../pages/StockChartStandalone/index.jsx'));
const PaymentHistoryPage = lazy(() => import('../pages/PaymentHistory/index.jsx'));
const UserSettingsPage = lazy(() => import('../pages/UserSettings/index.jsx'));

// Loading component
const LoadingFallback = () => (
  <div className="tw-flex tw-items-center tw-justify-center tw-h-screen">
    <div className="tw-text-center">
      <div className="tw-animate-spin tw-rounded-full tw-h-12 tw-w-12 tw-border-b-2 tw-border-blue-500 tw-mx-auto" />
      <p className="tw-mt-4 tw-text-gray-600">Loading...</p>
    </div>
  </div>
);

// Route configuration
const routes = [
  // Auth routes (without Layout) - GuestRoute: nếu đã login thì redirect về main
  {
    path: '/login',
    element: (
      <GuestRoute>
        <Suspense fallback={<LoadingFallback />}>
          <LoginPage />
        </Suspense>
      </GuestRoute>
    ),
  },
  {
    path: '/register',
    element: (
      <GuestRoute>
        <Suspense fallback={<LoadingFallback />}>
          <RegisterPage />
        </Suspense>
      </GuestRoute>
    ),
  },
  {
    path: '/auth/google/callback',
    element: (
      <Suspense fallback={<LoadingFallback />}>
        <GoogleCallbackPage />
      </Suspense>
    ),
  },
  // Breakout Tool - standalone page without main layout
  {
    path: '/breakout-tool',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <BreakoutToolPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  // Stock Chart Standalone - chỉ hiển thị TradingView chart cho API chụp màn hình
  {
    path: '/breakout-tool/stock-chart',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <StockChartStandalonePage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  // Stock Detail - standalone page without main layout
  {
    path: '/breakout-tool/stock/:symbol',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <StockDetailPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  // Payment History - standalone page without main layout
  {
    path: '/payment-history',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <PaymentHistoryPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  // Compare Plans - standalone page without main layout
  {
    path: '/compare-plans',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <ComparePlansPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  // User Settings - standalone page without main layout
  {
    path: '/user-settings',
    element: (
      <ProtectedRoute>
        <Suspense fallback={<LoadingFallback />}>
          <UserSettingsPage />
        </Suspense>
      </ProtectedRoute>
    ),
  },
  // Main app routes (with Layout and Protected)
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Layout />
      </ProtectedRoute>
    ),
    children: [
      {
        index: true,
        element: <Navigate to="/fintech-mock" replace />,
      },
      {
        path: 'fintech-mock',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FintechDashboardPage />
          </Suspense>
        ),
      },
      {
        path: 'portfolio-mock',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FeatureRoute feature={FEATURE_CODES.PORTFOLIO}>
              <PortfolioPage />
            </FeatureRoute>
          </Suspense>
        ),
      },
      {
        path: 'canslim-demo',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FeatureRoute feature={FEATURE_CODES.CANSLIM_PATTERNS}>
              <CanslimPatternsPage />
            </FeatureRoute>
          </Suspense>
        ),
      },
      {
        path: 'canslim-analysis',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FeatureRoute feature={FEATURE_CODES.CANSLIM}>
              <CanslimAnalysisPage />
            </FeatureRoute>
          </Suspense>
        ),
      },
      {
        path: 'persona-lens',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FeatureRoute feature={FEATURE_CODES.PERSONA}>
              <PersonaLensPage />
            </FeatureRoute>
          </Suspense>
        ),
      },
      {
        path: 'signals-alerts',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FeatureRoute feature={FEATURE_CODES.SIGNAL}>
              <SignalsAlertsPage />
            </FeatureRoute>
          </Suspense>
        ),
      },
      {
        path: 'search-stock',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FeatureRoute feature={FEATURE_CODES.SEARCH}>
              <SearchStockPage />
            </FeatureRoute>
          </Suspense>
        ),
      },
      {
        path: 'watchlist-notes',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FeatureRoute feature={FEATURE_CODES.WATCHLIST}>
              <WatchlistNotesPage />
            </FeatureRoute>
          </Suspense>
        ),
      },
      {
        path: 'video-studio',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <FeatureRoute feature={FEATURE_CODES.VIDEO}>
              <VideoStudioPage />
            </FeatureRoute>
          </Suspense>
        ),
      },
      {
        path: '*',
        element: (
          <Suspense fallback={<LoadingFallback />}>
            <NotFoundPage />
          </Suspense>
        ),
      },
    ],
  },
];

export default routes;
